'use client';

import RadioGroup from '../../../atoms/RadioGroup/RadioGroup';
import { getValidationResponse } from '../utils';
import type { RadioGroupField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type RadioGroupControlProps = {
  field: RadioGroupField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
};

/**
 * Form control for radio group fields
 * @component
 * @param {RadioGroupControlProps} props - Component props
 * @returns {JSX.Element} RadioGroup control component
 */
const RadioGroupControl = ({
  field,
  theme,
  direction,
  register,
  setValue,
  watch,
  errors,
}: RadioGroupControlProps) => {
  const currentValue = watch(field.name);

  // Map options to include default based on current value
  const optionsWithDefault = field.options.map((option) => ({
    ...option,
    default: option.value === currentValue,
  }));

  return (
    <RadioGroup
      key={`form_field_${field.name}`}
      theme={theme}
      name={field.name}
      label={field.label}
      helperText={field.helperText}
      required={field.required}
      options={optionsWithDefault}
      direction={direction}
      layout={field.layout}
      dataTestId={`radio-group-${field.name}`}
      gaComponent=""
      register={() =>
        register(field.name, {
          required: field.required ? field.validationMessages?.required : false,
        })
      }
      handleChange={(option) => setValue(field.name, option.value, { shouldValidate: true })}
      validationResponse={getValidationResponse({ field: field.name, errors })}
    />
  );
};

export default RadioGroupControl;
